<template>
	<v-alert
		v-if="missingSummary && !recovered"
		type="warning"
		variant="tonal"
		density="compact"
		rounded="lg"
		icon="mdi-alert-circle-outline"
		class="mt-2"
		data-test-id="session-media-shortfall-notice"
		v-test="'session-media-shortfall-notice'">
		<div class="text-body-2">
			<strong>Some media never finished uploading.</strong>
			This session was published without {{ missingSummary }}. That media was not recovered and needs re-capturing — reports and
			shares of this session cover only the media shown here.
		</div>
	</v-alert>
	<v-alert
		v-if="completenessUnverified"
		type="info"
		variant="tonal"
		density="compact"
		rounded="lg"
		class="mt-2"
		data-test-id="session-recording-completeness-notice"
		v-test="'session-recording-completeness-notice'">
		A recording is available, but we couldn't confirm that every part was received.
	</v-alert>
</template>

<script setup lang="ts">
	import { computed } from "vue";
	import type { MediaShortfall } from "@/types/Conversation";

	/**
	 * LSP-18274: the DURABLE marker, written once by contact-api when a conversation is released to
	 * Ready For Review with uploads still outstanding (the 120-minute staleness valve, the give-up
	 * path, or a knowingly-partial recording finalize).
	 *
	 * This is deliberately NOT the same signal as SessionIncompleteUploadNotice, which is recomputed
	 * live from a pending-upload predicate and disappears the moment the valve stops counting. This
	 * one describes the published artefact and stays visible forever, which is the whole point of the
	 * ticket: a released conversation must not look healthy when it is missing footage.
	 *
	 * LSP-22572 Task 4e: when the missing upload later completes and nothing is lost any more, contact-api
	 * stamps `recoveredAt` and keeps the original counts. The warning is then hidden, so the page stops claiming
	 * the video never finished.
	 */
	const props = defineProps<{
		mediaShortfall?: MediaShortfall | null;
		completenessUnverified?: boolean;
	}>();

	const BUCKETS: Array<{ key: "pendingRecordings" | "pendingSnapshots" | "pendingFiles"; singular: string; plural: string }> = [
		{ key: "pendingRecordings", singular: "video recording", plural: "video recordings" },
		{ key: "pendingSnapshots", singular: "photo", plural: "photos" },
		{ key: "pendingFiles", singular: "file", plural: "files" }
	];

	const missingSummary = computed(() => {
		const shortfall = props.mediaShortfall;
		if (!shortfall) return "";
		const parts = BUCKETS.filter((bucket) => Number(shortfall[bucket.key]) > 0).map((bucket) => {
			const count = Number(shortfall[bucket.key]);
			return `${count} ${count === 1 ? bucket.singular : bucket.plural}`;
		});
		if (parts.length === 0) return "";
		if (parts.length === 1) return parts[0];
		return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
	});

	const recovered = computed(() => Boolean(props.mediaShortfall?.recoveredAt));
</script>
